import PropTypes from "prop-types";
import styles from "./EscrowContainer.module.css";
import { useEffect, useRef, useState, useCallback } from "react";
import NewCompanyModel from "../KYCList/AddEscrow/NewCompanyModel";
import SelectActivity1 from "../SelectActivity/SelectActivity1";
import config from "../../../service/config";
import {
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TablePagination,
  TableRow,
  TextField,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { Spin } from "antd";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import axios from "axios";
import { useAllProduct } from "../../api-context/commonApiContext";

const PageContainer = () => {
  const [data, setData] = useState([]);
  const { authTokens, role, userData, refreshData, loader, setLoader, clientID, isTokenValid, getNewTokens } =
    useAllProduct();

  const [showModel, setShowModel] = useState(false);
  const [compName, setCompName] = useState("");
  const [errorMsg, setErrorMsg] = useState("");
  const [showActivity, setShowActivity] = useState(false);
  const [currentcompid, setCurrentCompId] = useState("");
  const [createdDateFilter, setCreatedDateFilter] = useState(null);
  const [textFilter, setTextFilter] = useState("");
  const [currentCompName, setCurrentCompName] = useState("");

  const [filteredData, setFilteredData] = useState([]);
  const [searchedByDate, setSearchedByDate] = useState([]);

  const [currentPage, setCurrentPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const modalRef = useRef(null);

  /** ---------------------------------------------------------
   *  FIX: Memoize fetchData so it's stable inside useEffect
   * -------------------------------------------------------- */
  const fetchData = useCallback(async () => {
    try {
      setLoader(true);

      let activeToken = authTokens?.idToken;

      if (!isTokenValid(activeToken)) {
        await getNewTokens();
        const updated = JSON.parse(localStorage.getItem("authTokens"));
        activeToken = updated?.idToken;
      }

      if (!activeToken) {
        setLoader(false);
        return;
      }

      const escrowRes = await axios.get(`${config.node_api_endpoint}/v1/escrow`, {
        headers: {
          aws_cognito_id_token: activeToken,
          "X-Client-Id": clientID,
          "Content-Type": "application/json",
        },
      });

      const escrowData = escrowRes.data;

      const updatedEscrowData = escrowData.map((escrow) => {
        const user = userData?.find((u) => u.email === escrow.created_by);
        return {
          ...escrow,
          role: user ? user?.auth[0]?.role?.map((r) => r.toString()) : "N/A",
        };
      });

      const sorted = updatedEscrowData.sort((a, b) => {
        const dateA = dayjs(a.created_at, "DD MMM YYYY / hh:mm a");
        const dateB = dayjs(b.created_at, "DD MMM YYYY / hh:mm a");
        return dateB - dateA;
      });

      setData(updatedEscrowData);
      setFilteredData(sorted);
    } catch (e) {
      console.error("Fetch failed:", e);
    } finally {
      setLoader(false);
    }
  }, [clientID, userData, isTokenValid, getNewTokens, setLoader]);

  useEffect(() => {
    // Wait until clientID is fully loaded
    if (role?.length > 0 && userData?.length > 0 && clientID !== "" && clientID !== null && authTokens?.idToken) {
      fetchData();
    }
  }, [role, userData, clientID, authTokens?.idToken]);

  useEffect(() => {
    fetchData();
  }, [refreshData]);

  useEffect(() => {
    if (showModel || showActivity) {
      document.addEventListener("mousedown", handleClickOutside);
    }
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, [showModel, showActivity]);

  const handleClickOutside = (event) => {
    if (modalRef.current && !modalRef.current.contains(event.target)) {
      setShowModel(false);
      setShowActivity(false);
      setCompName("");
      setErrorMsg("");
    }
  };

  const handleTextFilterChange = (e) => {
    const value = e.target.value.toLowerCase();
    setTextFilter(value);

    let filtered = searchedByDate.length ? searchedByDate : data;

    if (value) {
      filtered = filtered.filter(
        (item) =>
          item?.created_at?.toLowerCase().includes(value) ||
          item?.created_by?.toLowerCase().includes(value) ||
          item?.name?.toLowerCase().includes(value) ||
          item?.role?.toString()?.toLowerCase().includes(value),
      );
    }

    setFilteredData(filtered);
    setCurrentPage(0);
  };

  const applyFilters = () => {
    let filtered = [...data];

    if (createdDateFilter) {
      filtered = filtered.filter((item) => {
        const [datePart] = item.created_at.split(" / ");
        return dayjs(datePart).format("DD-MM-YYYY") === dayjs(createdDateFilter).format("DD-MM-YYYY");
      });
    }

    setSearchedByDate(filtered);
    setFilteredData(filtered);
    setCurrentPage(0);
  };

  const currentPageData = filteredData.slice(currentPage * rowsPerPage, currentPage * rowsPerPage + rowsPerPage);

  const handleScrollToTop = () => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  return (
    <div className={`${styles.pageContainer} ${styles.family} h-100 w-100`}>
      <Spin
        size="large"
        tip="Loading..."
        spinning={loader}
        className="d-flex justify-content-center align-items-center w-100 h-100"
      >
        <div
          className={`${styles.newCompany_div} ${
            ["Owner", "Super Admin", "Admin"].includes(role?.toString()) ? "" : styles.disabledDiv
          }`}
        >
          <button
            onClick={() => setShowModel(true)}
            className={styles.newCompany}
            disabled={!["Owner", "Super Admin", "Admin"].includes(role?.toString())}
          >
            <img className={styles.plusCircleIcon} alt="" src="/pluscircle.svg" />
            <b className={styles.addNewCompany}>Add Escrow account</b>
          </button>
        </div>

        {/* Filters */}
        <div className={`${styles.searchFiltersParent} mt-3 p-2 mb-3`}>
          <div className={styles.frameParent}>
            <div className={styles.leadingIconParent}>
              <img className={styles.leadingIcon} alt="" src="/leadingicon.svg" />
              <TextField label="Search" variant="standard" value={textFilter} onChange={handleTextFilterChange} />
            </div>

            <div className={styles.mobileMenuParent}>
              <LocalizationProvider dateAdapter={AdapterDayjs}>
                <DatePicker
                  label="Created Date"
                  value={createdDateFilter}
                  maxDate={dayjs()}
                  onChange={(v) => setCreatedDateFilter(v)}
                  sx={{
                    "& .MuiInputBase-root": {
                      borderRadius: "12px",
                    },
                  }}
                />
              </LocalizationProvider>

              <div className={styles.loadsButtons1} onClick={applyFilters}>
                <img className={styles.boxArrowInDownIcon} alt="" src="/boxarrowindown1.svg" />
                <b className={styles.addNewCompany}>Apply</b>
              </div>
            </div>
          </div>
        </div>

        {/* Table */}
        <Paper>
          <TableContainer>
            <Table>
              <TableHead className={styles.tableBg}>
                <TableRow>
                  <TableCell align="center">Created Date</TableCell>
                  <TableCell align="center">Escrow Name</TableCell>
                  <TableCell align="center">Created By</TableCell>
                  <TableCell align="center">Role</TableCell>
                  <TableCell align="center">Action</TableCell>
                </TableRow>
              </TableHead>

              <TableBody>
                {currentPageData.length ? (
                  currentPageData.map((detail) => (
                    <TableRow key={detail._id}>
                      <TableCell align="center">{detail.created_at}</TableCell>

                      <TableCell align="center">
                        {detail.name.length > 30 ? detail.name.slice(0, 30) + "..." : detail.name}
                      </TableCell>

                      <TableCell align="center">{detail.created_by}</TableCell>

                      <TableCell align="center">{detail.role?.toString?.() || "N/A"}</TableCell>

                      <TableCell align="center">
                        <Button
                          className={styles.change}
                          style={{
                            color: "#2348AB",
                            textDecoration: "underline",
                            textTransform: "lowercase",
                          }}
                          onClick={() => {
                            handleScrollToTop();
                            setShowActivity(true);
                            setCurrentCompId(detail.id);
                            setCurrentCompName(detail.name);
                          }}
                        >
                          View More
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={5} align="center">
                      No Records Found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>

          <TablePagination
            className={styles.tableBg}
            rowsPerPageOptions={[5, 10, 25]}
            component="div"
            count={filteredData.length}
            rowsPerPage={rowsPerPage}
            page={currentPage}
            onPageChange={(e, p) => setCurrentPage(p)}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setCurrentPage(0);
            }}
          />
        </Paper>

        {/* Modals */}
        <SelectActivity1
          isVisible={showActivity}
          onclose={() => setShowActivity(false)}
          escrowId={currentcompid}
          escrowName={currentCompName}
          modalRef={modalRef}
        />

        <NewCompanyModel
          isVisible={showModel}
          onclose={() => setShowModel(false)}
          setLoader={setLoader}
          clientID={clientID}
          modalRef={modalRef}
          compName={compName}
          setCompName={setCompName}
          errorMsg={errorMsg}
          setErrorMsg={setErrorMsg}
        />
      </Spin>
    </div>
  );
};

PageContainer.propTypes = {
  className: PropTypes.string,
};

export default PageContainer;
